import { ModalViewModel } from '@/infra/ModalViewModel'
import { type CardBillingMethodEditorViewModel } from '@/features/billing/profile/CardBillingMethodEditor/CardBillingMethodEditorViewModel'
import { action, observable } from 'mobx'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { filterNonNil } from '@/utils/rx-util'
import { switchMap } from 'rxjs'
import { task } from 'mobx-task'

/**
 * View model for the new billing method dialog.
 */
export class NewBillingMethodDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel()

  /**
   * Emits when a billing method has been created.
   */
  readonly created$ = toStream(() => this.cardBillingMethodEditorVm, true).pipe(
    filterNonNil(),
    switchMap((vm) => vm.created$),
  )

  /**
   * The current card method editor.
   */
  @observable
  accessor cardBillingMethodEditorVm: CardBillingMethodEditorViewModel | null =
    null

  constructor(
    public readonly createCardBillingMethodEditorVm: () => CardBillingMethodEditorViewModel,
  ) {}

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Saves the billing method.
   */
  save = task.resolved(async () => {
    if (await this.cardBillingMethodEditorVm?.save()) {
      this.modal.close()
    }
  })

  /**
   * Starts the process of creating a new method.
   */
  @action.bound
  newMethod() {
    this.cardBillingMethodEditorVm = this.createCardBillingMethodEditorVm()
    this.modal.show()
  }
}
